// Render the current product mockup to a PNG image and download it.
// Composites: colored background (optional) + mockup image (blend multiply) +
// design overlay (with position + rotation) + logo watermark (bottom-right).

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = src;
  });

/**
 * @param {Object} opts
 * @param {string} opts.mockupImageUrl       Absolute URL of the mockup image (background)
 * @param {string} [opts.designSrc]          Absolute URL or data: URL of the design overlay
 * @param {{top:number,left:number,width:number,height:number,rotation:number}} [opts.designPos]
 *                                            Position of the design in % (0-100) of the mockup area
 * @param {string} [opts.colorHex]           Optional color tint applied under the mockup image
 * @param {string} [opts.blendMode]          e.g. "multiply" for the design over the mockup
 * @param {string} [opts.logoUrl]            Absolute URL of the brand logo (watermark bottom-right)
 * @param {string} [opts.watermarkText]      Optional text watermark drawn under the logo
 * @param {number} [opts.size]               Output canvas size in px (square). Default 1400.
 * @param {string} [opts.filename]           Suggested download filename
 */
export async function downloadMockupPreview({
  mockupImageUrl,
  designSrc,
  designPos,
  colorHex,
  blendMode = "multiply",
  logoUrl,
  watermarkText,
  size = 1400,
  filename = "mockup.png",
}) {
  const [mockupImg, designImg, logoImg] = await Promise.all([
    loadImage(mockupImageUrl),
    designSrc ? loadImage(designSrc) : Promise.resolve(null),
    logoUrl ? loadImage(logoUrl).catch(() => null) : Promise.resolve(null),
  ]);

  if (!mockupImg) throw new Error("No se pudo cargar la imagen del mockup");

  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");

  // 1. Color tint background (if any)
  if (colorHex) {
    ctx.fillStyle = colorHex;
    ctx.fillRect(0, 0, size, size);
  } else {
    ctx.fillStyle = "#f5f5f5";
    ctx.fillRect(0, 0, size, size);
  }

  // 2. Draw mockup image (cover-fit, centered). When a color tint is applied, use
  //    multiply so the tint colors the mockup like the DOM does with mix-blend-mode.
  const mw = mockupImg.naturalWidth;
  const mh = mockupImg.naturalHeight;
  const scale = Math.max(size / mw, size / mh);
  const drawW = mw * scale;
  const drawH = mh * scale;
  const drawX = (size - drawW) / 2;
  const drawY = (size - drawH) / 2;
  ctx.globalCompositeOperation = colorHex ? "multiply" : "source-over";
  ctx.drawImage(mockupImg, drawX, drawY, drawW, drawH);
  ctx.globalCompositeOperation = "source-over";

  // 3. Design overlay
  if (designImg && designPos) {
    const pxTop = (designPos.top / 100) * size;
    const pxLeft = (designPos.left / 100) * size;
    const pxWidth = (designPos.width / 100) * size;
    const pxHeight = (designPos.height / 100) * size;
    const cx = pxLeft + pxWidth / 2;
    const cy = pxTop + pxHeight / 2;
    ctx.save();
    ctx.translate(cx, cy);
    if (designPos.rotation) ctx.rotate((designPos.rotation * Math.PI) / 180);
    ctx.globalCompositeOperation = blendMode || "source-over";
    // Preserve aspect: fit inside pxWidth x pxHeight
    const dw = designImg.naturalWidth || 1;
    const dh = designImg.naturalHeight || 1;
    const s = Math.min(pxWidth / dw, pxHeight / dh);
    const fw = dw * s;
    const fh = dh * s;
    ctx.drawImage(designImg, -fw / 2, -fh / 2, fw, fh);
    ctx.restore();
    ctx.globalCompositeOperation = "source-over";
  }

  // 4. Logo watermark (bottom-right, semi-transparent) — always draw a text pill,
  // and if a logo image loaded successfully add the logo above the text.
  {
    const margin = size * 0.025;
    const pad = size * 0.014;
    const textSize = Math.floor(size * 0.024);
    const label = (watermarkText || "").trim();
    let finalW = 0;
    let finalH = 0;

    if (logoImg) {
      const wmMaxWidth = size * 0.18;
      const lw = logoImg.naturalWidth || 1;
      const lh = logoImg.naturalHeight || 1;
      const lscale = Math.min(wmMaxWidth / lw, (size * 0.08) / lh);
      finalW = lw * lscale;
      finalH = lh * lscale;
    }

    // Measure text width to size the pill
    ctx.save();
    ctx.font = `bold ${textSize}px Helvetica, Arial, sans-serif`;
    const textWidth = label ? ctx.measureText(label).width : 0;
    ctx.restore();

    const contentW = Math.max(finalW, textWidth);
    const contentH = finalH + (label ? textSize + pad * 0.5 : 0);
    const bw = contentW + pad * 2;
    const bh = contentH + pad * 2;
    const bx = size - bw - margin;
    const by = size - bh - margin;
    const r = size * 0.008;

    // Draw pill (white translucent)
    ctx.save();
    ctx.globalAlpha = 0.88;
    ctx.fillStyle = "rgba(255,255,255,0.94)";
    ctx.beginPath();
    ctx.moveTo(bx + r, by);
    ctx.lineTo(bx + bw - r, by);
    ctx.quadraticCurveTo(bx + bw, by, bx + bw, by + r);
    ctx.lineTo(bx + bw, by + bh - r);
    ctx.quadraticCurveTo(bx + bw, by + bh, bx + bw - r, by + bh);
    ctx.lineTo(bx + r, by + bh);
    ctx.quadraticCurveTo(bx, by + bh, bx, by + bh - r);
    ctx.lineTo(bx, by + r);
    ctx.quadraticCurveTo(bx, by, bx + r, by);
    ctx.closePath();
    ctx.fill();
    // Subtle border
    ctx.strokeStyle = "rgba(0,0,0,0.08)";
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();

    // Logo (if any)
    if (logoImg && finalW > 0) {
      ctx.save();
      ctx.globalAlpha = 0.95;
      ctx.drawImage(logoImg, bx + (bw - finalW) / 2, by + pad, finalW, finalH);
      ctx.restore();
    }

    // Brand text (always drawn if we have a label)
    if (label) {
      ctx.save();
      ctx.font = `bold ${textSize}px Helvetica, Arial, sans-serif`;
      ctx.fillStyle = "rgba(15,15,15,0.9)";
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      const ty = by + pad + finalH + (finalH ? pad * 0.4 : 0);
      ctx.fillText(label, bx + bw / 2, ty);
      ctx.restore();
    }
  }

  // 5. Trigger download
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) return reject(new Error("No se pudo generar la imagen"));
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      resolve(true);
    }, "image/png");
  });
}
